import { load } from '@cashfreepayments/cashfree-js'
import { isProd } from 'v2/configs/constants'

type Cashfree = Awaited<ReturnType<typeof load>>

let cashfreePromise: ReturnType<typeof load> | null = null

// Memoize so the SDK script is fetched once per session and one instance is
// shared across recharge attempts. On rejection, drop the cached promise so a
// retry on the next click re-fetches instead of failing forever.
export const getCashfree = () => {
  if (!cashfreePromise) {
    cashfreePromise = load({ mode: isProd ? 'production' : 'sandbox' }).catch((error) => {
      cashfreePromise = null
      throw error
    })
  }
  return cashfreePromise
}

// The SDK instance narrowed to the web-components API (`create`/`pay` present).
export type CashfreeWithComponents = Cashfree & {
  create: NonNullable<Cashfree['create']>
  pay: NonNullable<Cashfree['pay']>
}

// Not every cashfree-js build / merchant config exposes the web-components API
// (`create`/`pay`). Narrow to the callable shape so the card-elements flow can
// bail out with a clear message instead of throwing a cryptic runtime error.
export const supportsCardComponents = (cf: Cashfree): cf is CashfreeWithComponents =>
  typeof cf.create === 'function' && typeof cf.pay === 'function'

export interface CashfreeCardComponents {
  cardNumber: ReturnType<NonNullable<Cashfree['create']>>
  cardExpiry: ReturnType<NonNullable<Cashfree['create']>>
  cardCvv: ReturnType<NonNullable<Cashfree['create']>>
  cardHolder: ReturnType<NonNullable<Cashfree['create']>>
  save: ReturnType<NonNullable<Cashfree['create']>>
}

// Build the four card-entry components + the save-consent component. The caller
// mounts each into its own DOM node and passes `cardNumber` + `save` to `cf.pay`.
export const createCardComponents = (cf: CashfreeWithComponents): CashfreeCardComponents => ({
  cardNumber: cf.create('cardNumber', {}),
  cardExpiry: cf.create('cardExpiry', {}),
  cardCvv: cf.create('cardCvv', {}),
  cardHolder: cf.create('cardHolder', {}),
  save: cf.create('savePaymentInstrument', {
    values: { label: 'Save this card for future payments' },
  }),
})

export interface CardElementsPaymentDeps {
  pay: CashfreeWithComponents['pay']
  verifyAndPoll: (orderId: string) => Promise<string>
  paymentSessionId: string
  orderId: string
  paymentMethod: CashfreeCardComponents['cardNumber']
  saveComponent: CashfreeCardComponents['save']
}

export type CardElementsPaymentResult =
  | { outcome: 'error'; error?: { code?: string; message: string; type?: string } }
  | { outcome: 'settled'; paymentStatus: string }

// Orchestrates the browser-side card payment: hand the card + consent components
// to cf.pay, then confirm the real outcome server-side. Pure (all deps injected)
// so it is unit-testable without React or mocking shared modules. Saving happens
// only because `saveComponent` is forwarded to the SDK — never a hardcoded flag.
export const runCardElementsPayment = async (
  deps: CardElementsPaymentDeps,
): Promise<CardElementsPaymentResult> => {
  const result = await deps.pay({
    paymentSessionId: deps.paymentSessionId,
    paymentMethod: deps.paymentMethod,
    savePaymentInstrument: deps.saveComponent,
  })
  // cf.pay's browser result is not a reliable success signal — only its `error`
  // is. On error, stop before touching balance/cards; the caller shows a toast.
  if (result?.error) {
    return { outcome: 'error', error: result.error }
  }
  const paymentStatus = await deps.verifyAndPoll(deps.orderId)
  return { outcome: 'settled', paymentStatus }
}
